import { MessageSquare } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import type { DocumentCommentCounts } from "@/stores/documents/comments/types";

interface CommentCountBadgeProps {
  counts: DocumentCommentCounts | undefined;
  /** Opens the comments panel on this document. Undefined renders a static badge. */
  onClick?: () => void;
  className?: string;
}

/**
 * The per-row comment mark beside the filename: a speech bubble with the
 * OPEN count. Renders nothing for a document with no threads at all; a
 * document whose threads are all completed shows the bubble with no number.
 */
export function CommentCountBadge({ counts, onClick, className }: CommentCountBadgeProps) {
  const { t } = useLanguage();
  if (!counts || counts.open + counts.resolved === 0) return null;
  const c = t.documents.comments;
  const tooltip = `${c.openCount.replace("{count}", String(counts.open))} · ${c.resolvedCount.replace(
    "{count}",
    String(counts.resolved)
  )}`;
  const label = c.rowBadge.replace("{count}", String(counts.open));
  const body = (
    <>
      <MessageSquare className="h-3.5 w-3.5" aria-hidden />
      {counts.open > 0 && (
        <span className="text-[11px] font-medium tabular-nums">{counts.open}</span>
      )}
    </>
  );
  const classes = cn(
    "inline-flex shrink-0 items-center gap-0.5 rounded px-1 text-muted-foreground",
    onClick && "hover:bg-muted hover:text-foreground",
    className
  );
  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          {onClick ? (
            <button
              type="button"
              aria-label={label}
              className={classes}
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              {body}
            </button>
          ) : (
            <span aria-label={label} className={classes}>
              {body}
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
